import { useEffect, useState } from "react";
import {
  createBrowserRouter,
  Navigate,
  Route,
  RouterProvider,
  Routes,
  useLocation,
} from "react-router-dom";
import { BrandingProvider } from "@/components/BrandingProvider";
import { Layout } from "@/components/layout/Layout";
import { Toaster } from "@/components/ui";
import { Dashboard } from "@/pages/Dashboard";
import { Login } from "@/pages/Login";
import { Register } from "@/pages/Register";
import { Settings } from "@/pages/Settings";
import { Customers } from "@/pages/Customers";
import { CustomerDetail } from "@/pages/Customers/CustomerDetail";
import { Properties } from "@/pages/Properties";
import { Orders } from "@/pages/Orders";
import { Quotes } from "@/pages/Quotes";
import { QuoteForm } from "@/pages/Quotes/QuoteForm";
import { Invoices } from "@/pages/Invoices";
import { InvoiceForm } from "@/pages/Invoices/InvoiceForm";
import { Articles } from "@/pages/Articles";
import { Shops } from "@/pages/Shops";
import { Contracts } from "@/pages/Contracts";
import { ContractForm } from "@/pages/Contracts/ContractForm";
import { Tax } from "@/pages/Tax";
import { Expenses } from "@/pages/Expenses";
import { Platform } from "@/pages/Platform";
import { isClientError, trpc } from "@/lib/trpc";
import { hasEnteredCrm, resetCrmEntered } from "@/lib/platformEntry";

// Daten-Router (statt <BrowserRouter>): nur damit lässt sich Navigation abfangen (useBlocker), um bei
// ungespeicherten Eingaben nachzufragen — siehe lib/useUnsavedGuard.tsx.
function Shell() {
  const { pathname } = useLocation();
  const access = trpc.platform.access.useQuery(undefined, { retry: false });

  // Plattform-Verwaltung: eigene Oberfläche ohne CRM-Menü
  if (pathname.startsWith("/plattform")) return <Platform />;

  // Betreiber landen nach der Anmeldung zuerst in der Plattform-Verwaltung; das CRM öffnet erst „Testumgebung"
  if (pathname === "/") {
    if (access.isLoading)
      return (
        <div className="h-full flex items-center justify-center bg-bg-primary">
          <span className="text-sm text-text-tertiary">Lädt …</span>
        </div>
      );
    if (access.data?.allowed && !hasEnteredCrm())
      return <Navigate to="/plattform" replace />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/kunden" element={<Customers />} />
        <Route path="/kunden/:id" element={<CustomerDetail />} />
        <Route path="/objekte" element={<Properties />} />
        <Route path="/auftraege" element={<Orders />} />
        <Route path="/einstellungen" element={<Settings />} />
        <Route path="/angebote" element={<Quotes />} />
        <Route path="/angebote/neu" element={<QuoteForm />} />
        <Route path="/angebote/:id" element={<QuoteForm />} />
        <Route path="/rechnungen" element={<Invoices />} />
        <Route path="/rechnungen/neu" element={<InvoiceForm />} />
        <Route path="/rechnungen/:id" element={<InvoiceForm />} />
        <Route path="/vertraege" element={<Contracts />} />
        <Route path="/vertraege/neu" element={<ContractForm />} />
        <Route path="/vertraege/:id" element={<ContractForm />} />
        <Route path="/artikel" element={<Articles />} />
        <Route path="/shops" element={<Shops />} />
        <Route path="/steuer" element={<Tax />} />
        <Route path="/ausgaben" element={<Expenses />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

const router = createBrowserRouter([{ path: "*", element: <Shell /> }]);

export default function App() {
  const utils = trpc.useUtils();
  // Bei Netzfehlern (nicht bei "nicht angemeldet") erneut versuchen — sonst landet man bei einem
  // kurzen Verbindungsabbruch fälschlich auf der Login-Seite.
  const me = trpc.auth.me.useQuery(undefined, {
    retry: (count, err) => count < 3 && !isClientError(err),
  });
  const [authMode, setAuthMode] = useState<"login" | "register">("login");
  const signupEnabled = trpc.auth.signupEnabled.useQuery(undefined, {
    retry: false,
  });
  const runDue = trpc.contract.runDue.useMutation({
    onSuccess: (r) => {
      if (r.runs > 0) {
        utils.contract.list.invalidate();
        utils.task.list.invalidate();
        utils.dashboard.summary.invalidate();
        utils.invoice.list.invalidate();
        utils.quote.list.invalidate();
      }
    },
  });

  useEffect(() => {
    if (me.data) runDue.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.data?.org.id]);

  if (me.isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-bg-primary">
        <span className="text-sm text-text-tertiary">Lädt …</span>
      </div>
    );
  }

  if (!me.data) {
    const canRegister = signupEnabled.data?.enabled ?? false;
    return (
      <>
        {authMode === "register" && canRegister ? (
          <Register
            onSuccess={() => utils.auth.me.invalidate()}
            onBack={() => setAuthMode("login")}
          />
        ) : (
          <Login
            onSuccess={() => {
              resetCrmEntered();
              utils.auth.me.invalidate();
            }}
            onRegister={canRegister ? () => setAuthMode("register") : undefined}
          />
        )}
        <Toaster />
      </>
    );
  }

  return (
    <BrandingProvider>
      <RouterProvider router={router} />
    </BrandingProvider>
  );
}
